import {
  type PdfPageBaseLayout,
  Button,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { ESIGN_PAGE_WIDTH } from "@/pages/esign/esign.config.ts";
import { EsignPagesViewer } from "@/pages/esign/components/EsignPagesViewer.tsx";
import { EsignSignFieldsLayer } from "@/pages/esign/components/EsignSignFieldsLayer.tsx";
import { EsignDeclineModal } from "@/pages/esign/widgets/EsignDeclineModal.tsx";
import {
  getFieldKey,
  isSignableField,
  isSignerFillableField,
} from "@/pages/esign/esign-sign.util.ts";
import { useEsignRequestSourceUrl } from "@/pages/esign/use-esign-request-source.hook.ts";
import { useEsignRequestSign, useEsignSigningContext } from "@/queries/use-esign.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEsignField } from "@/types/esign.types.ts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface EsignSigningModalProps extends ModalProps {
  requestId: string;
}

export function EsignSigningModal({ requestId, ...props }: EsignSigningModalProps) {
  const { t } = useTranslation();
  const { close, openModal } = useModals();
  const { id } = props;

  const { data: context, isError: isContextError } = useEsignSigningContext(requestId);
  const request = context?.request;
  const {
    url: documentUrl,
    isLoading: isDocumentLoading,
    isError: isDocumentError,
  } = useEsignRequestSourceUrl(requestId);
  const { mutate: signRequest, isPending } = useEsignRequestSign();

  useEffect(() => {
    if (isContextError || isDocumentError) close(id);
  }, [isContextError, isDocumentError, close, id]);

  const [isStarted, setIsStarted] = useState(false);
  const [hasViewerError, setHasViewerError] = useState(false);
  const [signedKeys, setSignedKeys] = useState<Set<string>>(new Set());
  const [valueEdits, setValueEdits] = useState<Record<string, string>>({});
  const fieldRefs = useRef<Map<string, HTMLElement>>(new Map());
  const lastNavKeyRef = useRef<string | null>(null);

  const roleFields = useMemo<ApiEsignField[]>(() => {
    if (!context) return [];
    return context.roles.filter((role) => role.is_current_role).flatMap((role) => role.fields);
  }, [context]);

  const otherFields = useMemo<ApiEsignField[]>(() => {
    if (!context) return [];
    return context.roles.filter((role) => !role.is_current_role).flatMap((role) => role.fields);
  }, [context]);

  const fillableFields = useMemo(() => roleFields.filter(isSignerFillableField), [roleFields]);

  const fieldValues = useMemo(() => {
    const map: Record<string, string> = {};
    for (const field of fillableFields) {
      const key = getFieldKey(field);
      map[key] = key in valueEdits ? valueEdits[key] : (field.value ?? "");
    }
    return map;
  }, [fillableFields, valueEdits]);

  const gateFields = useMemo(() => {
    return roleFields
      .filter(
        (field) => isSignableField(field) || (isSignerFillableField(field) && field.is_required)
      )
      .slice()
      .sort((a, b) => a.page - b.page || a.pos_y - b.pos_y || a.pos_x - b.pos_x);
  }, [roleFields]);

  const navFields = useMemo(() => {
    return roleFields
      .filter((field) => isSignableField(field) || isSignerFillableField(field))
      .slice()
      .sort((a, b) => a.page - b.page || a.pos_y - b.pos_y || a.pos_x - b.pos_x);
  }, [roleFields]);

  const isFieldReady = (field: ApiEsignField) =>
    isSignableField(field)
      ? signedKeys.has(getFieldKey(field))
      : (fieldValues[getFieldKey(field)] ?? "").trim() !== "";

  const pendingKeys = gateFields.filter((field) => !isFieldReady(field)).map(getFieldKey);
  const unsignedCount = pendingKeys.length;
  const allSigned = unsignedCount === 0;
  const fieldsTotal = gateFields.length;
  const signedProgress = fieldsTotal > 0 ? (fieldsTotal - unsignedCount) / fieldsTotal : 0;

  const navPendingKeys = navFields.filter((field) => !isFieldReady(field)).map(getFieldKey);
  const hasNextField = navPendingKeys.length > 0;

  const scrollToKey = (key: string) => {
    fieldRefs.current.get(key)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const goToFieldKey = (key: string) => {
    lastNavKeyRef.current = key;
    scrollToKey(key);
  };

  const handleStart = () => {
    setIsStarted(true);
    const firstKey = navPendingKeys[0];
    if (firstKey) window.setTimeout(() => goToFieldKey(firstKey), 50);
  };

  const handleSign = useCallback((key: string) => {
    setSignedKeys((prev) => new Set(prev).add(key));
  }, []);

  const handleChangeValue = useCallback((key: string, value: string) => {
    setValueEdits((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleNext = () => {
    if (navPendingKeys.length === 0) return;
    const currentIndex = lastNavKeyRef.current ? navPendingKeys.indexOf(lastNavKeyRef.current) : -1;
    const nextKey = navPendingKeys[(currentIndex + 1) % navPendingKeys.length];
    goToFieldKey(nextKey);
  };

  const handleFinish = () => {
    if (isPending) return;
    const fieldValuesPayload = fillableFields
      .map((field) => ({
        field_key: getFieldKey(field),
        value: fieldValues[getFieldKey(field)] ?? "",
      }))
      .filter((item) => item.value.trim() !== "");
    signRequest(
      { requestId, payload: { field_values: fieldValuesPayload } },
      {
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.esign.signed, {
            [analyticProperties.id]: requestId,
          });
          close(id);
        },
      }
    );
  };

  const handleDecline = () => {
    openModal(EsignDeclineModal, { requestId, onDeclined: () => close(id) });
  };

  const fieldsByPage = useMemo(() => {
    const map = new Map<number, ApiEsignField[]>();
    for (const field of roleFields) {
      const list = map.get(field.page);
      if (list) list.push(field);
      else map.set(field.page, [field]);
    }
    return map;
  }, [roleFields]);

  const otherFieldsByPage = useMemo(() => {
    const map = new Map<number, ApiEsignField[]>();
    for (const field of otherFields) {
      const list = map.get(field.page);
      if (list) list.push(field);
      else map.set(field.page, [field]);
    }
    return map;
  }, [otherFields]);

  const registerFieldRef = useCallback((key: string, el: HTMLElement | null) => {
    if (el) fieldRefs.current.set(key, el);
    else fieldRefs.current.delete(key);
  }, []);

  const renderPageOverlay = useCallback(
    (pageNumber: number, baseLayout: PdfPageBaseLayout) => (
      <EsignSignFieldsLayer
        baseLayout={baseLayout}
        fields={fieldsByPage.get(pageNumber - 1) ?? []}
        disabledFields={otherFieldsByPage.get(pageNumber - 1) ?? []}
        signedKeys={signedKeys}
        values={fieldValues}
        onSign={handleSign}
        onChangeValue={handleChangeValue}
        registerFieldRef={registerFieldRef}
      />
    ),
    [
      fieldsByPage,
      otherFieldsByPage,
      signedKeys,
      fieldValues,
      handleSign,
      handleChangeValue,
      registerFieldRef,
    ]
  );

  const isLoading = !context || isDocumentLoading;

  const hasStarted = isStarted || signedKeys.size > 0 || Object.keys(valueEdits).length > 0;

  const declineButton = (
    <Button size={"lg"} variant={"plain"} disabled={isPending} onClick={handleDecline}>
      {t("core:esign_decline")}
    </Button>
  );

  const actions = isLoading ? undefined : !hasStarted ? (
    <Stack direction={"horizontal"} justify={"between"} className={"w-full"}>
      {declineButton}
      <Button size={"lg"} onClick={handleStart}>
        {t("core:esign_start_signing")}
      </Button>
    </Stack>
  ) : (
    <Stack
      direction={"horizontal"}
      items={"center"}
      justify={"end"}
      gap={"lg"}
      className={"w-full"}
    >
      <div className={"mr-auto"}>{declineButton}</div>
      {!allSigned ? (
        <Text className={"text-secondary text-right"}>
          {t("core:esign_unsigned_fields_left", { count: unsignedCount })}
        </Text>
      ) : (
        <Text className={"text-secondary max-w-120 text-right"}>{request?.intent_statement}</Text>
      )}
      {hasNextField && (
        <Button size={"lg"} variant={allSigned ? "secondary" : "primary"} onClick={handleNext}>
          {t("core:esign_next_field")}
        </Button>
      )}
      {allSigned && (
        <Button size={"lg"} isLoading={isPending} disabled={isPending} onClick={handleFinish}>
          {t("core:esign_finish_signing")}
        </Button>
      )}
    </Stack>
  );

  return (
    <Modal
      {...props}
      title={t("core:esign_signing")}
      caption={request?.document_title}
      variant={"middle"}
      layout={"stretch"}
      size={"full"}
      withCloser={true}
      actions={hasViewerError ? undefined : actions}
    >
      <div>
        {!hasViewerError && (
          <div className={"sticky top-0 z-30 -mx-6 h-1 bg-dark/10"}>
            <div
              className={"h-full bg-dark transition-[width] duration-300 ease-out"}
              style={{ width: `${signedProgress * 100}%` }}
            />
          </div>
        )}
        {isLoading || !documentUrl ? (
          <Skeleton className={"mx-auto h-215 w-full max-w-170"} />
        ) : (
          <EsignPagesViewer
            src={documentUrl}
            pageNavigationPlacement={"left"}
            maxRenderWidth={ESIGN_PAGE_WIDTH}
            renderPageOverlay={renderPageOverlay}
            onErrorStateChange={setHasViewerError}
          />
        )}
      </div>
    </Modal>
  );
}
